import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { DeviceSecretNotice } from "@/components/envSecrets/DeviceSecretNotice";
import { InputField } from "@/components/common/InputField";
import { TextInputField } from "@/components/common/TextInputField";
import { createSecret, updateSecret, type Secret } from "@/lib/api";
import { domainSyntaxError } from "@/lib/domain-validation";
import { INPUT_STYLE, focusRingOff, focusRingOn } from "@/lib/form-styles";
import { parseHostList, secretNameError } from "@/lib/secret-validation";

interface SecretDialogProps {
	open: boolean;
	environmentId: string;
	onDevice: boolean;
	/** 編集対象。null なら新規作成。 */
	secret: Secret | null;
	onClose: () => void;
	onSaved: (secret: Secret) => void;
}

/**
 * シークレットの作成・編集ダイアログ。名前は作成後に変えられない (環境変数名として保存済みの
 * bash セッションに残るため)。値は保存後に読み出せないので、編集では空欄 = 変更なし、入力 = 上書き。
 */
export function SecretDialog({
	open,
	environmentId,
	onDevice,
	secret,
	onClose,
	onSaved,
}: SecretDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const editing = secret !== null;
	const [name, setName] = useState("");
	const [value, setValue] = useState("");
	const [hostsText, setHostsText] = useState("");
	const [nameError, setNameError] = useState<string | null>(null);
	const [valueError, setValueError] = useState<string | null>(null);
	const [hostsError, setHostsError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) el.showModal();
		else if (!open && el.open) el.close();
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setName(secret?.name ?? "");
		setValue("");
		setHostsText(secret?.allowedHosts.join("\n") ?? "");
		setNameError(null);
		setValueError(null);
		setHostsError(null);
		setSubmitError(null);
	}, [open, secret]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				if (!loading) onClose();
			}
		},
		[onClose, loading],
	);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const trimmedName = name.trim();
		const hosts = parseHostList(hostsText);
		const nextNameError = editing ? null : secretNameError(trimmedName);
		const nextValueError =
			!editing && value.length === 0
				? "シークレット値は必須です。"
				: /[\r\n\0]/.test(value)
					? "シークレット値に改行は使えません。"
					: null;
		const nextHostsError =
			hosts.length === 0
				? "許可ホストを 1 件以上指定してください。"
				: (hosts.map(domainSyntaxError).find((err) => err !== null) ?? null);
		setNameError(nextNameError);
		setValueError(nextValueError);
		setHostsError(nextHostsError);
		if (nextNameError || nextValueError || nextHostsError) return;

		setSubmitError(null);
		setLoading(true);
		try {
			const saved = editing
				? await updateSecret(environmentId, secret.id, {
						value: value.length > 0 ? value : undefined,
						allowedHosts: hosts,
					})
				: await createSecret(environmentId, { name: trimmedName, value, allowedHosts: hosts });
			onSaved(saved);
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="secret-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-popover)",
				background: "var(--surface-1)",
				color: "var(--fg-1)",
				width: 520,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "16px 24px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
						}}
					>
						{editing ? `シークレットを編集: ${secret.name}` : "シークレットを作成"}
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					{!editing && (
						<TextInputField
							label="シークレット名 (環境変数名)"
							value={name}
							onChange={(v) => setName(v.toUpperCase())}
							placeholder="例: GITHUB_TOKEN"
							autoComplete="off"
							error={nameError ?? undefined}
						/>
					)}
					<TextInputField
						label={editing ? "新しい値 (空欄なら変更しない)" : "シークレット値"}
						value={value}
						onChange={setValue}
						type="password"
						// Chrome はパスワード欄の off を無視して保存済み認証情報を流し込むため、new-password で抑止する。
						autoComplete="new-password"
						error={valueError ?? undefined}
					/>
					<InputField label="許可ホスト (1 行に 1 つ)" error={hostsError ?? undefined}>
						<textarea
							value={hostsText}
							onChange={(e) => setHostsText(e.target.value)}
							placeholder={"例:\napi.github.com\n*.example.com"}
							spellCheck={false}
							data-testid="secret-hosts-textarea"
							style={{
								...INPUT_STYLE,
								display: "block",
								height: 110,
								padding: "8px 10px",
								fontFamily: "var(--font-mono)",
								fontSize: "var(--text-caption)",
								lineHeight: "var(--leading-body)",
								resize: "vertical",
							}}
							onFocus={(e) => focusRingOn(e.currentTarget)}
							onBlur={(e) => focusRingOff(e.currentTarget)}
						/>
					</InputField>
					<span
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--fg-2)",
							lineHeight: "var(--leading-body)",
							fontFamily: "var(--font-sans)",
						}}
					>
						エージェントの bash
						とターミナルには、この名前の環境変数として値の代わりにプレースホルダが注入されます。
						許可ホストへの HTTP(S)
						リクエストに含まれるプレースホルダだけが送信時に実値へ置き換わります。{" "}
						<span style={{ color: "var(--danger)", fontWeight: 500 }}>
							値は保存後に表示できません。
						</span>
					</span>
					{onDevice && <DeviceSecretNotice />}
					{submitError && (
						<div
							data-testid="secret-dialog-error"
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						padding: "12px 24px",
						borderTop: "var(--pane-header-border)",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						label={editing ? "保存" : "作成"}
						loading={loading}
						data-testid="secret-dialog-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}
